import { useEffect } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import * as QuestionService from "../../../services/questionService";
import type { Question, QuestionOption } from "../../../types/onboarding";
import styles from "./ProfileSelectQuestion.module.css";

interface ProfileSelectQuestionProps {
	question: Question;
	currentValue: string;
	onInputChange: (field: string, value: string | number | string[]) => void;
}

export function ProfileSelectQuestion({
	question,
	currentValue,
	onInputChange,
}: ProfileSelectQuestionProps) {
	const { t } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();

	// Extract question logic to service
	const getOptionText = (option: QuestionOption) =>
		QuestionService.getProfileOptionText(option, t);

	const primaryOption = QuestionService.getPrimaryProfileOption(question);
	const secondaryOptions = QuestionService.getSecondaryProfileOptions(question);

	useEffect(() => {
		if (!currentValue && primaryOption) {
			onInputChange(question.field, primaryOption.value);
		}
	}, [currentValue, primaryOption, onInputChange, question.field]);

	const handleOptionClick = (option: QuestionOption) => {
		logEvent("profile_option_clicked", undefined, {
			option_value: option.value,
			option_label: getOptionText(option),
			field_name: question.field,
			was_selected: (currentValue === option.value).toString(),
		});

		onInputChange(question.field, option.value);
	};

	const renderButton = (option: QuestionOption, isPrimary = false) => {
		const isSelected = currentValue === option.value;

		return (
			<button
				key={option.value}
				type="button"
				onClick={() => handleOptionClick(option)}
				className={`${styles.button} ${isPrimary ? styles.buttonPrimary : styles.buttonSecondary} ${isSelected ? styles.buttonSelected : styles.buttonUnselected}`}
			>
				{getOptionText(option)}
			</button>
		);
	};

	return (
		<div className={styles.container}>
			{primaryOption && (
				<div className={styles.primaryButtonWrapper}>
					{renderButton(primaryOption, true)}
				</div>
			)}

			{secondaryOptions && secondaryOptions.length > 0 && (
				<div className={styles.secondaryButtonsWrapper}>
					{secondaryOptions.map((option) => renderButton(option, false))}
				</div>
			)}
		</div>
	);
}
